import { ChannelData, MediaCount } from "../types";
import Card from "./card/Card";
import { useCallback, useEffect, useRef, useState } from "react";
import { baseUrl, postKmsData } from "@kaltura/mediaspace-shared-utils";
import { StyledSearchResultsContainer } from "../styled-components";

export interface CardsContainerProps {
    /**
     * channels data to render
     */
    channels: ChannelData[];
}

const CardsContainer = (props: CardsContainerProps) => {
    const { channels } = props;

    const [mediaCount, setMediaCount] = useState<MediaCount>({});

    const makeRequest = useCallback(async () => {
        try {
            return await postKmsData(
                `${baseUrl}/default/channels/get-media-count`,
                { channelIds: channels.map((channel) => channel.id) }
            );
        }
        catch (e) {
            console.debug("default/channels/get-media-count failed", e);
            return undefined;
        }
    }, [channels]);

    useEffect(() => {
        // fetch media count data from kms
        (async () => {
            const response = await makeRequest();
            if (response?.mediaCount) {
                setMediaCount(response.mediaCount);
            }
        })();
    }, [makeRequest]);

    const gridRef = useRef<HTMLDivElement>(null);
    const gridItemRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const gridItems = gridItemRefs.current;
            const activeElement = document.activeElement as HTMLElement;
            const currentIndex = gridItems.findIndex((item) => item && item.contains(activeElement));

            if (currentIndex === -1) {
                // ignore if focus is outside grid
                return;
            }

            if (e.key === "ArrowDown" || e.key === "ArrowRight") {
                if (currentIndex < gridItems.length - 1) {
                    e.preventDefault();
                    gridItems[currentIndex + 1]?.focus();
                }
            }
            else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
                if (currentIndex > 0) {
                    e.preventDefault();
                    gridItems[currentIndex - 1]?.focus();
                }
            }
        };

        const grid = gridRef.current;
        grid?.addEventListener("keydown", handleKeyDown);

        return () => grid?.removeEventListener("keydown", handleKeyDown);
    }, []);

    return (
        <div ref={gridRef} role={"grid"}>
            <StyledSearchResultsContainer role={"row"}>
                {channels.map((channel, index) => (
                    <Card
                        key={channel.id}
                        mediaCount={mediaCount[channel.id]}
                        ref={(el: HTMLDivElement | null) => (gridItemRefs.current[index] = el)}
                        {...channel}
                    />
                ))}
            </StyledSearchResultsContainer>
        </div>
    );
};

export default CardsContainer;
